import { on } from '@ember/modifier';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { task } from 'reactiveweb/ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Select from 'tio-reimagined/components/select';
import TuitionAssistanceProgramFormField from '../program-form-field.gts';
import { OpenSearchQueryBuilder } from '../../../utils/open-search-builder';
import { OpenSearchResponseBuilder } from '../../../utils/open-search-response-builder';
import type { TaskInstance } from 'reactiveweb/ember-concurrency';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import type TasUnifiedInstitutionModel from 'tio-data/models/tas-unified-institution';
import type { SearchAdapter, TasUnifiedInstitution } from 'tio-data/models/search';
import { or } from 'tio-ui/utilities';

const INPUT_DEBOUNCE = 250;
const MIN_SEARCH_LENGTH = 2;

export interface TuitionAssistanceFormsSchoolSelectSignature {
  Args: {
    programTemplate: TasProgramTemplateModel;
    selected?: TasUnifiedInstitutionModel | TasUnifiedInstitution | null;
    onChange?: (value?: TasUnifiedInstitution) => void;
    placeholder?: string;
    label?: string;
    labelClass?: string;
    errors?: string[];
  };
  Element: HTMLDivElement;
}

export default class TuitionAssistanceFormsSchoolSelectComponent extends Component<TuitionAssistanceFormsSchoolSelectSignature> {
  @service declare store: StoreService;
  @tracked filterTerm = '';
  @tracked searchResults: TasUnifiedInstitution[] = [];

  fetchRecords = restartableTask(async (term: string) => {
    await timeout(INPUT_DEBOUNCE);

    // Require minimum characters to avoid noise and improve relevance
    if (!term || term.length < MIN_SEARCH_LENGTH) {
      return [];
    }

    // Build OpenSearch query for TAS unified institutions
    const queryBuilder = new OpenSearchQueryBuilder().setPagination(1, 100);

    this.buildInstitutionSearchQuery(queryBuilder, term);

    const searchQuery = {
      'model-type': 'TasUnifiedInstitution',
      'search-body': queryBuilder.build(),
      'program-template-id': this.args.programTemplate.id!, // Pass as separate parameter
    };

    const response = await (
      this.store.adapterFor('search') as unknown as SearchAdapter
    ).search<TasUnifiedInstitution>(searchQuery);

    // Use ResponseBuilder to extract institutions from OpenSearch response
    const responseBuilder = new OpenSearchResponseBuilder(response);
    const documents = responseBuilder.getDocuments().filter((doc) => !!doc.identifier);

    // Store the full documents for later use
    this.searchResults = documents;

    return documents.map((doc) => ({
      key: doc.identifier,
      label: doc.locationName,
    }));
  });

  /**
   * Build a consistent search query that provides progressive narrowing as the user types.
   *
   * Strategy:
   * 1. match_phrase_prefix: Primary query - results narrow progressively as user types
   *    "uni" -> "univ" -> "university" all match "University of California"
   * 2. word_start: Edge ngram matching for each word - handles partial words well
   *    "univ calif" matches "University of California"
   * 3. text_middle: Substring matching (ngram) - catches mid-word matches
   *    Only for 3+ chars to avoid overwhelming results
   *
   * This unified strategy avoids the "disappearing results" bug where different
   * strategies at different character counts caused inconsistent behavior.
   */
  private buildInstitutionSearchQuery(queryBuilder: OpenSearchQueryBuilder, term: string) {
    // Primary: phrase prefix for progressive typing - results narrow as you type more
    // "uni" matches "University...", typing "univ" narrows further, etc.
    queryBuilder.addShould({
      match_phrase_prefix: { 'location_name.analyzed': term },
    });

    // Secondary: word_start for each word in the query
    // This handles multi-word queries like "univ calif" well
    // and provides good prefix matching via edge ngrams
    queryBuilder.addShould({
      match: {
        'location_name.word_start': {
          query: term,
          operator: 'and',
          boost: 2.0,
        },
      },
    });

    // Tertiary: text_middle for substring matches (ngram analyzer)
    // Only enable for 3+ characters to avoid overwhelming broad matches
    if (term.length >= 3) {
      queryBuilder.addShould({
        match: {
          'location_name.text_middle': {
            query: term,
            boost: 0.5,
          },
        },
      });
    }

    queryBuilder.setMinimumShouldMatch(1);
  }

  fetchRecordsTask: TaskInstance<Array<{ key: string; label: string }>> = task(
    this,
    this.fetchRecords,
    () => [this.filterTerm]
  );

  private getInstitutionKey(
    institution: TasUnifiedInstitutionModel | TasUnifiedInstitution | null | undefined
  ): string | null {
    if (!institution) return null;

    // Check for TasUnifiedInstitutionModel (has id)
    if ('id' in institution && institution.id) {
      return institution.id;
    }

    // Check for TasUnifiedInstitution (has identifier)
    if ('identifier' in institution && institution.identifier) {
      return institution.identifier;
    }

    return null;
  }

  get items() {
    // Show previous results while loading to avoid "no results found" flash
    const results: { key: string; label: string }[] = this.fetchRecordsTask.isRunning
      ? (this.fetchRecordsTask.value ?? [])
      : (this.fetchRecordsTask.value ?? []);

    const selected = this.args.selected;

    // Handle both TasUnifiedInstitutionModel (has .id) and TasUnifiedInstitution (has .identifier)
    const selectedKey = this.getInstitutionKey(selected);

    if (!selectedKey) {
      return results;
    }

    // Find the corresponding OpenSearch result for the selected model/institution
    const selectedFromSearch = this.searchResults.find(
      (result) => result.identifier === selectedKey
    );

    if (selectedFromSearch) {
      const selectedInstitution = {
        key: selectedFromSearch.identifier,
        label: selectedFromSearch.locationName,
      };
      const otherInstitutions = results.filter(
        (item) => item.key !== selectedFromSearch.identifier
      );
      return [selectedInstitution, ...otherInstitutions];
    }

    // If not found in search results, add the selected model/institution to the list
    const selectedInstitution = { key: selectedKey, label: selected?.locationName ?? '' };
    const otherInstitutions = results.filter((item) => item.key !== selectedKey);
    return [selectedInstitution, ...otherInstitutions];
  }

  get selectedKey() {
    return this.getInstitutionKey(this.args.selected);
  }

  onFilterChange = (term: string) => {
    this.filterTerm = term;
  };

  handleSelectionChange = async (keys: string | null) => {
    if (!keys) {
      this.filterTerm = '';
      this.args.onChange?.();
    } else {
      // Find the selected institution from the search results instead of making an API call
      const selectedInstitution = this.searchResults.find((result) => result.identifier === keys);
      if (selectedInstitution) {
        this.args.onChange?.(selectedInstitution);
      }
    }
  };

  <template>
    <TuitionAssistanceProgramFormField
      @name="SCHOOL_INSTITUTION_NAME"
      @programTemplate={{@programTemplate}}
      @labelClass={{@labelClass}}
      ...attributes
      as |field|
    >
      <Select
        data-test={{field.name}}
        @id={{field.inputId}}
        @name="SCHOOL_INSTITUTION_NAME"
        @placeholder={{or @placeholder "Begin Typing to See Results..."}}
        @searchable={{true}}
        @isLoading={{this.fetchRecordsTask.isRunning}}
        @items={{this.items}}
        @selected={{this.selectedKey}}
        @onSearch={{this.onFilterChange}}
        @onChange={{this.handleSelectionChange}}
        @clearable={{true}}
        @errors={{if field.isTouched @errors}}
        @searchDebounce={{0}}
        {{on "focusout" field.markTouched}}
      />
    </TuitionAssistanceProgramFormField>
  </template>
}
